import * as React from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { TFOrderInCart } from '~graphql/generated/clientGraphql'
import { TGroupedItem } from '~logic/helpers/groupUtils/groupItems'
import { AmountControl } from '~ui/components/formElements/AmountControl/AmountControl'
import { Box } from '~ui/components/layout/Box'

import { useCartFetchingCtx, useCartUpdateCtx } from '../contexts/CartContentProvider'
import { getItemsToRemove } from '../helpers/sortOrderItems'

type TProps = NoChildren & {
  item: TGroupedItem<TFOrderInCart['items'][number]>
}

// let the user play with the amount for some time, then update the BE count accordingly
// either by adding more items with the same property values or by removing some of the items from the cart
const ITEM_COUNT_EDIT_DEBOUNCE_DELAY_MS = 500

/**
 * Debounced AmountControl bound to the CartProvider.
 * Use to change the amount of items with the same property values in the cart.
 */
export const CartItemAmountControl: React.FC<TProps> = ({ item }) => {
  const isCartFetching = useCartFetchingCtx()
  const { addProductToCart, removeCartItems } = useCartUpdateCtx()

  // the number showed in the cart
  const [counterValue, setCounterValue] = React.useState(item.count)

  // update the item count on the BE
  const updateCountOnBe = React.useCallback(
    (newCount: number) => {
      const countVariance = newCount - item.count

      // if some items should be added, create new ones with the same values
      if (countVariance > 0) {
        addProductToCart({
          amount: countVariance,
          note: item.firstItem.note,
          addons: item.firstItem.addons.map((addon) => {
            return {
              recipeId: addon.product.id,
              categoryId: addon.category?.id ?? '',
            }
          }),
          product: item.firstItem.product,
          // there could be multiple items added from different places, it makes no sense to just randomly pick the promoTag and the origin category ID of one of them
          promoTag: null,
          categoryId: null,
        })

        return
      }

      // if some items should be removed, pick some of the item instance and remove them from the cart
      if (countVariance < 0) {
        const removedItemCount = Math.abs(countVariance)
        const removedItems = getItemsToRemove({ items: item.items, removedItemCount })

        // TODO check for empty array

        removeCartItems({ cartItems: removedItems })
      }
    },
    [addProductToCart, item.count, item.firstItem, item.items, removeCartItems],
  )

  // wait for some time to let the user add/delete multiple items without triggering the Ms
  const debouncedUpdateCountOnBe = useDebouncedCallback(
    updateCountOnBe,
    ITEM_COUNT_EDIT_DEBOUNCE_DELAY_MS,
  )

  // whenever we are done fetching, update the rendered amount to the real value that BE is aware of
  // (there can be some variations eg. if some M would fail)
  React.useEffect(() => {
    if (isCartFetching) {
      return
    }

    setCounterValue(item.count)
  }, [isCartFetching, item.count])

  return (
    <Box width={`7rem`}>
      <AmountControl
        allowDelete
        value={counterValue}
        isDisabled={item.firstItem.addedAutomatically || isCartFetching}
        onChange={(newValue) => {
          // user has deleted all the way to 0 -> fire the BE update immediately
          if (newValue === 0) {
            debouncedUpdateCountOnBe.cancel() // cancel any debounced callback we might’ve had from before
            setCounterValue(newValue)
            updateCountOnBe(newValue) // update immediately

            return
          }

          // otherwise update the rendered value & queue debounced BE update
          setCounterValue(newValue)
          debouncedUpdateCountOnBe(newValue)
        }}
      />
    </Box>
  )
}
